import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IPostModel,
  usePostManagementHook,
} from '../../../../hooks/admin/post/use-post';
import {
  fetchInvalidPostsAPI,
  fetchPostsApi,
  handleAdminPostApi,
  handleInvalidPostApi,
} from '../../../../api/admin/post.api';
import { promptTotp } from '../../../../utils/admin-stepup';
import toast from 'react-hot-toast';
import { Check, RefreshCw } from 'lucide-react';
import { AdminButton, AdminPageHeader, AdminPillTabs, AdminStatusPill } from '../../ui';
import { AdminPostModCard } from '../post-moderation/AdminPostParts';

const ManagementPendingPost = (props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isRefetch, setIsRefetch] = useState(false);
  const [status, setStatus] = useState('Uncheck');
  const [isInvalidTab, setIsInvalidTab] = useState(false);
  const [invalidPosts, setInvalidPosts] = useState<IPostModel[]>([]); // State for invalid posts
  const { data, hasMore } = usePostManagementHook({
    pageNumber,
    isRefetch,
    status,
  });
  const [lengthPostUnCheck, setLengthPostUnCheck] = useState(0);
  const [lengthPostInvalid, setLengthPostInvalid] = useState(0);

  const getAllPostUnCheck = async () => {
    try {
      const { data } = await fetchPostsApi(1, 'Uncheck');
      setLengthPostUnCheck(data.data.length);
    } catch {
      //
    } finally {
      //
    }
  };
  const getAllPostInvalid = async () => {
    try {
      const { data } = await fetchInvalidPostsAPI(1);
      setLengthPostInvalid(data.data.length);
      if (isInvalidTab) {
        setInvalidPosts(data.data); // Update invalid posts when tab is active
      }
    } catch {
      //
    } finally {
      //
    }
  };
  const handlePost = async (postId, status, reason?: string) => {
    const totp = promptTotp('duyệt bài viết');
    if (!totp) return;
    try {
      await handleAdminPostApi(postId, status, reason, totp);
      setIsRefetch(!isRefetch);
      toast.success('Thành công');
    } catch {
      //
    } finally {
      //
    }
  };

  // Từ chối kèm LÝ DO (bắt buộc nhập) — lưu để hiện ở tab "Đã từ chối".
  const handleReject = async (postId) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Nhập lý do từ chối bài viết:')?.trim();
    if (!reason) return; // huỷ hoặc bỏ trống → không từ chối
    await handlePost(postId, 'Rejected', reason);
  };

  const handleInvalidPost = async (postId, newStatus) => {
    try {
      await handleInvalidPostApi(postId, newStatus);
      // Remove the approved post from invalidPosts
      setInvalidPosts(invalidPosts.filter((post) => post.id !== postId));
      toast.success('Thành công');
      // Refresh counts for both tabs
      await getAllPostUnCheck();
      await getAllPostInvalid();
      // If on Uncheck tab, trigger a refetch to show the newly moved post
      if (status === 'Uncheck') {
        setIsRefetch(!isRefetch);
      }
    } catch {
      toast.error('Thất bại');
    }
  };

  // Handler for Invalid tab click
  const handleInvalidTabClick = async () => {
    setIsInvalidTab(true);
    setPageNumber(1);
    setStatus(''); // Clear status to avoid fetching via usePostManagementHook
    try {
      const { data } = await fetchInvalidPostsAPI(1);
      setInvalidPosts(data.data);
    } catch {
      toast.error('Không thể tải bài viết vi phạm');
    }
  };

  // Handler for other tabs
  const handleStatusTabClick = (newStatus) => {
    setIsInvalidTab(false);
    setPageNumber(1);
    setStatus(newStatus);
  };

  useEffect(() => {
    getAllPostUnCheck();
    getAllPostInvalid();
  }, []);

  // Highlight banned words in post content
  const highlightBannedWords = (content, bannedWords) => {
    let highlightedContent = content;
    if (bannedWords) {
      bannedWords.split(',').forEach((word) => {
        if (word) {
          // Ensure word is not empty
          const regex = new RegExp(`\\b${word}\\b`, 'gi');
          highlightedContent = highlightedContent.replace(
            regex,
            `<span style="background-color: yellow;">${word}</span>`
          );
        }
      });
    }
    return highlightedContent;
  };

  const activeTab = isInvalidTab ? 'invalid' : status === 'Rejected' ? 'Rejected' : 'Uncheck';
  const list = (isInvalidTab ? invalidPosts : data) || [];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý bài viết"
        description="Duyệt bài mới đăng trước khi bài xuất hiện trên bảng tin."
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <AdminPillTabs
          ariaLabel="Lọc bài viết"
          value={activeTab}
          onChange={(k) => (k === 'invalid' ? handleInvalidTabClick() : handleStatusTabClick(k))}
          items={[
            { key: 'Uncheck', label: 'Chờ duyệt', count: `${lengthPostUnCheck} bài viết` },
            { key: 'invalid', label: 'Vi phạm', count: `${lengthPostInvalid} bài viết` },
            { key: 'Rejected', label: 'Đã từ chối', count: 'Bài bị từ chối' },
          ]}
        />
        <AdminButton
          size={36}
          variant="soft"
          icon={<RefreshCw size={16} />}
          onClick={() => (isInvalidTab ? handleInvalidTabClick() : handleStatusTabClick(status))}
        >
          Tải lại
        </AdminButton>
      </div>
      <div className="adm-mod-grid">
        {list.map((post, index) => (
          <AdminPostModCard
            key={post.id || index}
            post={post}
            highlightWords={
              isInvalidTab && post.detectedBannedWords ? post.detectedBannedWords.split(',') : []
            }
            headExtra={
              status === 'Rejected' && !isInvalidTab ? (
                <AdminStatusPill tone="danger">Đã từ chối</AdminStatusPill>
              ) : undefined
            }
            extra={
              <>
                {isInvalidTab && post.detectedBannedWords && (
                  <p className="adm-strip adm-strip--danger" role="status">
                    <span>
                      <strong>Những từ vi phạm:</strong> {post.detectedBannedWords}
                    </span>
                  </p>
                )}
                {status === 'Rejected' && !isInvalidTab && (post as any).rejectReason && (
                  <p className="adm-strip adm-strip--danger" role="status">
                    <span>
                      <strong>Lý do từ chối:</strong> {(post as any).rejectReason}
                    </span>
                  </p>
                )}
              </>
            }
            actions={
              <>
                {status === 'Uncheck' && (
                  <>
                    <AdminButton size={32} variant="fill" onClick={() => handlePost(post.id, 'NotApproved')}>
                      Không công khai
                    </AdminButton>
                    <AdminButton size={32} variant="outline" onClick={() => handlePost(post.id, 'Approved')}>
                      Riêng tư
                    </AdminButton>
                    <AdminButton size={32} variant="danger" onClick={() => handleReject(post.id)}>
                      Từ chối
                    </AdminButton>
                    <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handlePost(post.id, 'Public')}>
                      Công khai
                    </AdminButton>
                  </>
                )}
                {status === 'Rejected' && (
                  <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handlePost(post.id, 'Public')}>
                    Khôi phục &amp; công khai
                  </AdminButton>
                )}
                {isInvalidTab && (
                  <>
                    <AdminButton size={32} variant="danger" onClick={() => handleInvalidPost(post.id, 'NotApproved')}>
                      Không duyệt
                    </AdminButton>
                    <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handleInvalidPost(post.id, 'Approved')}>
                      Duyệt
                    </AdminButton>
                  </>
                )}
              </>
            }
          />
        ))}
      </div>
      {/* Phân trang: bấm trang nào gọi trang đó (không tải hết 1 lần) */}
      {!isInvalidTab && (
        <div className="adm-pagination" style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className="adm-page-btn adm-page-btn--nav"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
          >
            Trang trước
          </button>
          <span className="adm-page-btn adm-page-btn--active">{pageNumber}</span>
          <button
            type="button"
            className="adm-page-btn adm-page-btn--nav"
            onClick={() => setPageNumber((p) => p + 1)}
            disabled={!hasMore}
          >
            Trang sau
          </button>
        </div>
      )}
    </div>
  );
};

export default ManagementPendingPost;
